/**
 * Utility per gestire le immagini da Strapi con Cloudinary
 */

/**
 * Ottiene l'URL dell'immagine dall'evento (compatibile con strapiService esistente)
 * @param {Object} event - Oggetto evento
 * @param {string} size - Dimensione desiderata ('thumbnail', 'small', 'medium', 'large', 'original')
 * @returns {string|null} - URL dell'immagine o null
 */
export const getEventImageUrl = (event, size = 'medium') => {
  if (!event) return null;

  // 1. Il strapiService mappa 'item.images' in 'event.image' (singolare)
  // Quindi dobbiamo controllare 'event.image' che contiene l'array
  if (event.image && Array.isArray(event.image) && event.image.length > 0) {
    const firstImage = event.image[0];
    
    // Se richiede una dimensione specifica e esiste nei formats
    if (size !== 'original' && firstImage.formats && firstImage.formats[size]) {
      return firstImage.formats[size].url;
    }
    
    // Fallback all'immagine originale
    return firstImage.url;
  }

  // 2. Controllo per immagine singola (nel caso ci sia un mapping diverso)
  if (event.image && typeof event.image === 'object' && !Array.isArray(event.image)) {
    // Se è un oggetto singolo con formats
    if (size !== 'original' && event.image.formats && event.image.formats[size]) {
      return event.image.formats[size].url;
    }
    
    // Fallback all'immagine originale
    return event.image.url;
  }

  // 3. Controllo per URL diretto
  if (event.image && typeof event.image === 'string') {
    return event.image;
  }

  // 4. Fallback a gallery se esiste
  if (event.gallery && Array.isArray(event.gallery) && event.gallery.length > 0) {
    const firstImage = event.gallery[0];
    if (typeof firstImage === 'string') {
      return firstImage;
    }
    if (firstImage.url) {
      return firstImage.url;
    }
  }

  return null;
};

/**
 * Ottiene tutte le dimensioni disponibili per un'immagine
 * @param {Object} event - Oggetto evento
 * @returns {Object} - Oggetto con tutte le dimensioni disponibili
 */
export const getEventImageSizes = (event) => {
  if (!event || !event.image) return {};

  let firstImage = null;

  // Trova la prima immagine
  if (Array.isArray(event.image) && event.image.length > 0) {
    firstImage = event.image[0];
  } else if (typeof event.image === 'object') {
    firstImage = event.image;
  }

  if (!firstImage) return {};

  const sizes = {
    original: firstImage.url
  };

  if (firstImage.formats) {
    Object.keys(firstImage.formats).forEach(format => {
      sizes[format] = firstImage.formats[format].url;
    });
  }

  return sizes;
};

/**
 * Genera un'immagine placeholder se non ci sono immagini
 * @param {Object} event - Oggetto evento
 * @returns {string} - URL del placeholder
 */
export const getPlaceholderImage = (event) => {
  // Placeholder basato sulla categoria
  const categoryMap = {
    'ballo': 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400&h=200&fit=crop&q=80',
    'teatro': 'https://images.unsplash.com/photo-1507924538820-ede94a04019d?w=400&h=200&fit=crop&q=80',
    'concerti': 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=400&h=200&fit=crop&q=80',
    'spettacoli': 'https://images.unsplash.com/photo-1507924538820-ede94a04019d?w=400&h=200&fit=crop&q=80',
    'festival': 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=400&h=200&fit=crop&q=80',
    'mostre': 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=200&fit=crop&q=80',
    'sport': 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=400&h=200&fit=crop&q=80',
    'gastronomia': 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=200&fit=crop&q=80',
    'cultura': 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400&h=200&fit=crop&q=80',
    'arte': 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=200&fit=crop&q=80',
    'cinema': 'https://images.unsplash.com/photo-1489599735985-eebbbcf4e27b?w=400&h=200&fit=crop&q=80',
    'workshop': 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=200&fit=crop&q=80',
    'conferenze': 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=400&h=200&fit=crop&q=80',
    'bambini': 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=400&h=200&fit=crop&q=80',
    'natura': 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=200&fit=crop&q=80',
    'sagre': 'https://images.unsplash.com/photo-1533900298318-6b8da08a523e?w=400&h=200&fit=crop&q=80',
    'mercati': 'https://images.unsplash.com/photo-1555529902-bf1bbcfee291?w=400&h=200&fit=crop&q=80'
  };

  // Trova la categoria dell'evento
  if (event.categories && event.categories.length > 0) {
    const categoryName = event.categories[0].name.toLowerCase();
    if (categoryMap[categoryName]) {
      return categoryMap[categoryName];
    }
  }

  // Placeholder di default
  return 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=400&h=200&fit=crop&q=80';
};
